import React, { useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import classNames from "classnames"

import { useThrottle } from "shared/lib/performance/useThrottle"
import { useEvent } from "shared/lib/useEvent"
import { IconSize } from "shared/types/Icon"
import { WithClassName } from "shared/types/WithClassName"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"

import { IconName } from "../icon/__generated__/IconName"
import "./styles/_expandable.scss"

export interface ExpandableProps extends WithClassName {
  children: React.ReactNode
  maxHeight: number | null
  expandLabel?: string
  collapseLabel?: string
  timeout?: number
  isExpanded?: boolean
  defaultExpanded?: boolean
  onExpand?: (isExpanded: boolean) => void
  isDisabled?: boolean
  /** Ref to the expand/collapse button */
  buttonRef?: React.RefObject<HTMLButtonElement | null>
  /** Minimum additional height beyond maxHeight required to show expand button */
  threshold?: number
}

export const Expandable = React.memo(
  React.forwardRef<HTMLDivElement, ExpandableProps>(
    (
      {
        children,
        maxHeight,
        expandLabel,
        collapseLabel,
        isExpanded,
        isDisabled,
        defaultExpanded = false,
        onExpand,
        buttonRef,
        threshold = 0,
        className
      },
      ref
    ) => {
      const { t } = useTranslation()
      const contentRef = useRef<HTMLDivElement>(null)
      const [internalExpanded, setInternalExpanded] = useState(defaultExpanded)
      const [contentHeight, setContentHeight] = useState(0)

      // Use controlled state if provided, otherwise use internal state
      const isControlled = isExpanded !== undefined
      const expanded = isControlled ? isExpanded : internalExpanded

      const resizeObserverRef = useRef<ResizeObserver | null>(null)

      const needsExpansion = useMemo(
        () => maxHeight !== null && contentHeight > maxHeight + threshold,
        [contentHeight, maxHeight, threshold]
      )

      const measureContent = useEvent(() => {
        if (!contentRef.current) return
        setContentHeight(contentRef.current.scrollHeight)
      })

      const throttledMeasure = useThrottle(measureContent, 100)

      useEffect(() => {
        if (!contentRef.current) return undefined

        resizeObserverRef.current = new ResizeObserver(() => {
          throttledMeasure()
        })

        resizeObserverRef.current.observe(contentRef.current)

        return () => {
          resizeObserverRef.current?.disconnect()
        }
      }, [throttledMeasure])

      const handleToggle = useEvent(() => {
        const newExpandedState = !expanded

        // Only update internal state if uncontrolled
        if (!isControlled) {
          setInternalExpanded(newExpandedState)
        }

        // Always call onExpand callback
        onExpand?.(newExpandedState)
      })

      const resolvedExpandLabel = expandLabel || t("General.ShowMore")
      const resolvedCollapseLabel = collapseLabel || t("General.ShowLess")

      const contentClassName = classNames("expandable__content", {
        "expandable__content--expanded": expanded,
        "expandable__content--collapsed": !expanded && needsExpansion
      })

      return (
        <div ref={ref} className={classNames("expandable", className)}>
          <div
            ref={contentRef}
            className={contentClassName}
            style={{
              height:
                !needsExpansion || !maxHeight || expanded
                  ? undefined
                  : maxHeight
            }}
          >
            {children}
          </div>

          {needsExpansion && (
            <div className="expandable__controls">
              <Button
                ref={buttonRef}
                variant={ButtonVariant.SECONDARY}
                onClick={handleToggle}
                className="btn--borderless"
                icon={
                  expanded ? IconName.SMALL_CARET_UP : IconName.SMALL_CARET_DOWN
                }
                iconSize={IconSize.XXXS}
                disabled={isDisabled}
              >
                {expanded ? resolvedCollapseLabel : resolvedExpandLabel}
              </Button>
            </div>
          )}
        </div>
      )
    }
  )
)
